import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/Icon';

const STATUS_TABS = [
  { key: 'all',     label: 'All'     },
  { key: 'pending', label: 'Pending', badge: 'badge-pending.png'  },
  { key: 'paid',    label: 'Paid',    badge: 'badge-paid.png'     },
  { key: 'overdue', label: 'Overdue', badge: 'badge-overdue.png'  },
];

const PAYMENT_METHODS = ['Cash', 'Card', 'Bank Transfer', 'Online'];

const CURRENCY_MAP = {
  GB: '£',  IE: '€',  DE: '€',  FR: '€',  ES: '€',  IT: '€',
  NL: '€',  PT: '€',  BE: '€',  CH: 'CHF ', AT: '€', FI: '€',
  US: '$',  CA: '$',  AU: 'A$', NZ: 'NZ$',
  ZA: 'R',  SE: 'kr', NO: 'kr', DK: 'kr',
  AE: 'AED ', SG: 'S$',
};
function currencySymbol(country) {
  return CURRENCY_MAP[country] || '£';
}

function StatusBadge({ status }) {
  const map = {
    pending: { icon: 'badge-pending.png', color: '#F39C12' },
    paid:    { icon: 'badge-paid.png',    color: '#2ECC71' },
    overdue: { icon: 'badge-overdue.png', color: '#D72638' },
  };
  const b = map[status] || map.pending;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <img src={'/icons/' + b.icon} alt={status} style={{ width: 16, height: 16 }} onError={(e) => e.target.style.display='none'} />
      <span style={{ fontSize: 12, color: b.color, fontWeight: 600, textTransform: 'capitalize' }}>{status}</span>
    </span>
  );
}

function InvoiceModal({ inv, trainer, clients, onClose }) {
  const client     = clients.find(c => String(c.id) === String(inv.client_id));
  const brandColor = trainer?.brand_color || '#D72638';
  const curr       = currencySymbol(trainer?.country);
  const bizName    = trainer?.business_name || trainer?.name || '';
  const invNum     = `INV-${String(inv.id).padStart(4, '0')}`;

  function handlePrint() {
    const w = window.open('', '_blank');
    const logoHtml = trainer?.brand_logo_url
      ? `<img src="${trainer.brand_logo_url}" style="height:52px;max-width:130px;object-fit:contain;" />`
      : `<div style="width:52px;height:52px;border-radius:8px;background:rgba(255,255,255,0.25);display:flex;align-items:center;justify-content:center;color:#fff;font-size:22px;font-weight:800;">${bizName.charAt(0)}</div>`;

    w.document.write(`<!DOCTYPE html><html><head><title>${invNum}</title><style>
      *{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:'Inter',sans-serif;background:#f9f9f9;color:#1a1a1a;}
      .header{background:${brandColor};padding:24px 32px;display:flex;align-items:center;gap:20px;}
      .biz{font-size:20px;font-weight:800;color:#fff;}
      .tagline{font-size:12px;color:rgba(255,255,255,0.75);margin-top:3px;}
      .inv-title{margin-left:auto;font-size:28px;font-weight:900;color:rgba(255,255,255,0.9);letter-spacing:3px;}
      .body{padding:32px;}
      .meta{display:flex;gap:32px;flex-wrap:wrap;margin-bottom:28px;}
      .meta-item label{font-size:11px;color:#888;display:block;margin-bottom:2px;text-transform:uppercase;}
      .meta-item span{font-size:14px;font-weight:600;}
      .section-label{font-size:11px;color:#888;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;margin-bottom:6px;}
      table{width:100%;border-collapse:collapse;margin-bottom:20px;}
      thead td{background:#f5f5f5;padding:10px 14px;font-size:12px;font-weight:600;color:#555;text-transform:uppercase;}
      tbody td{padding:14px;border-bottom:1px solid #eee;font-size:14px;}
      .total-label{font-size:12px;color:#888;}
      .total-amount{font-size:32px;font-weight:800;color:${brandColor};}
      .status{font-size:14px;font-weight:600;color:${inv.status==='paid'?'#2ECC71':inv.status==='overdue'?'#D72638':'#F39C12'};text-transform:capitalize;}
      @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
    </style></head><body>
      <div class="header">
        ${logoHtml}
        <div>
          <p class="biz">${bizName}</p>
          ${trainer?.brand_tagline ? `<p class="tagline">${trainer.brand_tagline}</p>` : ''}
        </div>
        <div class="inv-title">INVOICE</div>
      </div>
      <div class="body">
        <div class="meta">
          <div class="meta-item"><label>Invoice No.</label><span>${invNum}</span></div>
          <div class="meta-item"><label>Issue Date</label><span>${new Date(inv.created_at).toLocaleDateString('en-GB')}</span></div>
          ${inv.due_date ? `<div class="meta-item"><label>Due Date</label><span>${new Date(inv.due_date).toLocaleDateString('en-GB')}</span></div>` : ''}
          <div class="meta-item"><label>Status</label><span class="status">${inv.status}</span></div>
          ${inv.payment_method ? `<div class="meta-item"><label>Payment</label><span>${inv.payment_method}</span></div>` : ''}
        </div>
        <div class="section-label">Bill To</div>
        <div style="font-size:16px;font-weight:600;margin-bottom:24px;">${client ? `${client.first_name} ${client.last_name}` : 'No client assigned'}</div>
        <table>
          <thead><tr><td>Description</td><td style="text-align:right;">Amount</td></tr></thead>
          <tbody><tr><td>${inv.description || 'Training services'}</td><td style="text-align:right;font-weight:600;">${curr}${parseFloat(inv.amount).toFixed(2)}</td></tr></tbody>
        </table>
        <div style="text-align:right;">
          <div class="total-label">Total Due</div>
          <div class="total-amount">${curr}${parseFloat(inv.amount).toFixed(2)}</div>
        </div>
      </div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0 0 0 0' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--color-surface)', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ background: brandColor, padding: '20px 22px', borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', gap: 14 }}>
          {trainer?.brand_logo_url
            ? <img src={trainer.brand_logo_url} alt="" style={{ height: 48, maxWidth: 120, objectFit: 'contain', flexShrink: 0 }} />
            : <div style={{ width: 48, height: 48, borderRadius: 8, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, fontWeight: 800, flexShrink: 0 }}>{bizName.charAt(0)}</div>
          }
          <div style={{ flex: 1 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>{bizName}</div>
            {trainer?.brand_tagline && <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, marginTop: 2 }}>{trainer.brand_tagline}</div>}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 800, fontSize: 18, letterSpacing: 2 }}>INVOICE</div>
        </div>

        {/* Details */}
        <div style={{ padding: '24px 28px' }}>
          {/* Meta */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 22 }}>
            {[
              ['Invoice No.', `INV-${String(inv.id).padStart(4,'0')}`],
              ['Issue Date',  new Date(inv.created_at).toLocaleDateString()],
              inv.due_date && ['Due Date', new Date(inv.due_date).toLocaleDateString()],
              ['Status', null],
              inv.payment_method && ['Payment', inv.payment_method],
            ].filter(Boolean).map(([label, val]) => (
              <div key={label}>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
                {label === 'Status' ? <StatusBadge status={inv.status} /> : <div style={{ fontSize: 14, fontWeight: 600 }}>{val}</div>}
              </div>
            ))}
          </div>

          {/* Bill To */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>Bill To</div>
            <div style={{ fontWeight: 600 }}>
              {client ? `${client.first_name} ${client.last_name}` : 'No client assigned'}
            </div>
          </div>

          {/* Line items */}
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '9px 16px', borderBottom: '1px solid var(--color-border)', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              <span>Description</span><span>Amount</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '14px 16px', fontSize: 14 }}>
              <span style={{ color: 'var(--color-text)' }}>{inv.description || 'Training services'}</span>
              <span style={{ fontWeight: 700 }}>{curr}{parseFloat(inv.amount).toFixed(2)}</span>
            </div>
          </div>

          {/* Total */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>Total Due</div>
              <div style={{ fontSize: 30, fontWeight: 800, color: brandColor }}>
                {curr}{parseFloat(inv.amount).toFixed(2)}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handlePrint}
              style={{ flex: 1, minHeight: 42, background: brandColor, color: '#fff', border: 'none', borderRadius: 8, fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Print / Save PDF
            </button>
            <button onClick={onClose}
              style={{ minHeight: 42, padding: '0 20px', background: 'none', border: '1px solid var(--color-border)', borderRadius: 8, color: 'var(--color-text-muted)', fontFamily: 'var(--font)', fontSize: 14, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Invoices() {
  const { trainer } = useAuth();
  const curr = currencySymbol(trainer?.country);
  const [invoices,    setInvoices]    = useState([]);
  const [clients,     setClients]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState('all');
  const [showForm,    setShowForm]    = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [viewInv,     setViewInv]     = useState(null);
  const [form,        setForm]        = useState({ client_id: '', amount: '', description: '', status: 'pending', payment_method: '', due_date: '' });
  const [saving,      setSaving]      = useState(false);
  const [priceTiers,  setPriceTiers]  = useState([]);
  const [showPricing, setShowPricing] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierForm,    setTierForm]    = useState({ name: '', price: '', duration_minutes: '' });
  const [savingTier,  setSavingTier]  = useState(false);

  useEffect(() => {
    Promise.all([api.get('/invoices'), api.get('/clients'), api.get('/invoices/price-tiers')])
      .then(([inv, cli, tiers]) => { setInvoices(inv); setClients(cli); setPriceTiers(tiers); })
      .finally(() => setLoading(false));
  }, []);

  function openAdd() {
    setEditing(null);
    setForm({ client_id: '', amount: '', description: '', status: 'pending', payment_method: '', due_date: '' });
    setShowForm(true);
  }

  function openEdit(inv) {
    setEditing(inv);
    setForm({
      client_id: inv.client_id || '',
      amount: inv.amount,
      description: inv.description || '',
      status: inv.status,
      payment_method: inv.payment_method || '',
      due_date: inv.due_date ? inv.due_date.split('T')[0] : '',
    });
    setShowForm(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const updated = await api.put('/invoices/' + editing.id, form);
        const client = clients.find(c => String(c.id) === String(updated.client_id));
        setInvoices(prev => prev.map(i => i.id === updated.id ? { ...updated, first_name: client?.first_name, last_name: client?.last_name } : i));
      } else {
        const created = await api.post('/invoices', form);
        const client = clients.find(c => String(c.id) === String(created.client_id));
        setInvoices(prev => [{ ...created, first_name: client?.first_name, last_name: client?.last_name }, ...prev]);
      }
      setShowForm(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function markPaid(inv) {
    const updated = await api.put('/invoices/' + inv.id, { ...inv, client_id: inv.client_id, status: 'paid' });
    setInvoices(prev => prev.map(i => i.id === updated.id ? { ...i, status: 'paid' } : i));
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this invoice?')) return;
    await api.delete('/invoices/' + id);
    setInvoices(prev => prev.filter(i => i.id !== id));
    setShowForm(false);
  }

  function applyTier(t) {
    setForm(f => ({ ...f, amount: t.price, description: t.name + (t.duration_minutes ? ` (${t.duration_minutes} min)` : '') }));
  }

  function openEditTier(t) {
    setEditingTier(t);
    setTierForm({ name: t.name, price: t.price, duration_minutes: t.duration_minutes || '' });
  }

  async function handleSaveTier(e) {
    e.preventDefault();
    setSavingTier(true);
    try {
      if (editingTier) {
        const updated = await api.put('/invoices/price-tiers/' + editingTier.id, tierForm);
        setPriceTiers(prev => prev.map(t => t.id === updated.id ? updated : t));
      } else {
        const created = await api.post('/invoices/price-tiers', tierForm);
        setPriceTiers(prev => [...prev, created]);
      }
      setEditingTier(null);
      setTierForm({ name: '', price: '', duration_minutes: '' });
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingTier(false);
    }
  }

  async function handleDeleteTier(id) {
    if (!window.confirm('Delete this pricing tier?')) return;
    await api.delete('/invoices/price-tiers/' + id);
    setPriceTiers(prev => prev.filter(t => t.id !== id));
    if (editingTier?.id === id) { setEditingTier(null); setTierForm({ name: '', price: '', duration_minutes: '' }); }
  }

  const filtered = tab === 'all' ? invoices : invoices.filter(i => i.status === tab);
  const total    = filtered.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);

  return (
    <div>
      <div className="page-header">
        <h1>
          <Icon name="icon-invoice.png" />
          Invoices
        </h1>
        <button className="icon-btn" onClick={openAdd}>
          <Icon name="icon-add.png" alt="Add" />
        </button>
      </div>

      {/* Status filter tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface)', overflowX: 'auto' }}>
        {STATUS_TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ flex: '0 0 auto', padding: '12px 16px', background: 'transparent', border: 'none',
              borderBottom: tab === t.key ? '2px solid var(--color-primary)' : '2px solid transparent',
              color: tab === t.key ? 'var(--color-primary)' : 'var(--color-text-muted)',
              fontFamily: 'var(--font)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            {t.badge && <img src={'/icons/' + t.badge} alt="" style={{ width: 14, height: 14 }} onError={(e) => e.target.style.display='none'} />}
            {t.label}
          </button>
        ))}
      </div>

      <div className="page-content">
        {showForm && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>{editing ? 'Edit Invoice' : 'New Invoice'}</div>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">Client</label>
                <select className="form-select" value={form.client_id} onChange={e => setForm(f => ({...f, client_id: e.target.value}))}>
                  <option value="">Select client</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                  ))}
                </select>
              </div>

              {priceTiers.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Quick Add from Pricing</label>
                  <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                    {priceTiers.map(t => (
                      <button key={t.id} type="button" onClick={() => applyTier(t)}
                        style={{ flexShrink: 0, padding: '8px 14px', background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font)', textAlign: 'left', transition: 'border-color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-primary)'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border)'}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', whiteSpace: 'nowrap' }}>{t.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--color-primary)', fontWeight: 500, marginTop: 2 }}>
                          {curr}{parseFloat(t.price).toFixed(2)}{t.duration_minutes ? ` · ${t.duration_minutes}min` : ''}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Amount *</label>
                <input className="form-input" type="number" step="0.01" min="0" placeholder="0.00"
                  value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" value={form.description}
                  onChange={e => setForm(f => ({...f, description: e.target.value}))}
                  placeholder="e.g. 4 x personal training sessions" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}>
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Method</label>
                  <select className="form-select" value={form.payment_method} onChange={e => setForm(f => ({...f, payment_method: e.target.value}))}>
                    <option value="">Select</option>
                    {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Due Date</label>
                <input className="form-input" type="date" value={form.due_date} onChange={e => setForm(f => ({...f, due_date: e.target.value}))} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving} style={{ flex: 1 }}>
                  {saving ? 'Saving...' : editing ? 'Save Changes' : 'Create Invoice'}
                </button>
                <button className="btn btn-ghost" type="button" style={{ flex: 1 }} onClick={() => setShowForm(false)}>Cancel</button>
              </div>
              {editing && (
                <button type="button" onClick={() => handleDelete(editing.id)}
                  style={{ width: '100%', marginTop: 8, minHeight: 44, background: 'rgba(215,38,56,0.1)', border: '1px solid rgba(215,38,56,0.3)', color: 'var(--color-primary)', borderRadius: 'var(--radius)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Delete Invoice
                </button>
              )}
            </form>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="card" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{filtered.length} invoice{filtered.length !== 1 ? 's' : ''}</span>
            <span style={{ fontWeight: 700, fontSize: 18 }}>{curr}{total.toFixed(2)}</span>
          </div>
        )}

        {loading ? (
          <div className="loading-spinner"><span className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Icon name="icon-invoice.png" size={48} style={{ opacity: 0.3 }} />
            <p>{tab === 'all' ? 'No invoices yet' : 'No ' + tab + ' invoices'}</p>
            {tab === 'all' && <button className="btn btn-primary" style={{ width: 'auto', padding: '0 24px' }} onClick={openAdd}>Create First Invoice</button>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map(inv => (
              <div key={inv.id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => openEdit(inv)}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>
                      {inv.first_name ? inv.first_name + ' ' + inv.last_name : 'No client'}
                    </div>
                    {inv.description && <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 2 }}>{inv.description}</div>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 18 }}>{curr}{parseFloat(inv.amount).toFixed(2)}</div>
                    <StatusBadge status={inv.status} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                    {new Date(inv.created_at).toLocaleDateString()}
                    {inv.due_date && <span> · Due {new Date(inv.due_date).toLocaleDateString()}</span>}
                    {inv.payment_method && <span> · {inv.payment_method}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button onClick={() => setViewInv(inv)}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', color: 'var(--color-text-muted)', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 12, fontWeight: 500 }}>
                      View
                    </button>
                    {inv.status !== 'paid' && (
                      <button onClick={e => { e.stopPropagation(); markPaid(inv); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.3)', color: 'var(--color-success)', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 12, fontWeight: 600 }}>
                        Mark Paid
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pricing Tiers */}
        <div className="card" style={{ margin: '16px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showPricing ? 16 : 0 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>Pricing</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>Save pricing tiers to quickly fill invoices</div>
            </div>
            <button onClick={() => setShowPricing(p => !p)}
              style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600, padding: '6px 0' }}>
              {showPricing ? 'Hide' : (priceTiers.length > 0 ? `Manage (${priceTiers.length})` : '+ Set Up')}
            </button>
          </div>

          {showPricing && (
            <div>
              {priceTiers.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  {priceTiers.map(t => (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-text)' }}>{t.name}</div>
                        <div style={{ fontSize: 13, color: 'var(--color-primary)', fontWeight: 500, marginTop: 2 }}>
                          {curr}{parseFloat(t.price).toFixed(2)}{t.duration_minutes ? ` · ${t.duration_minutes} min` : ''}
                        </div>
                      </div>
                      <button onClick={() => openEditTier(t)}
                        style={{ background: 'none', border: '1px solid var(--color-border)', color: 'var(--color-text-muted)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 12 }}>
                        Edit
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={handleSaveTier}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {editingTier ? 'Edit Tier' : 'New Tier'}
                </div>
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input className="form-input" value={tierForm.name} onChange={e => setTierForm(f => ({...f, name: e.target.value}))}
                    placeholder="e.g. 1-to-1 PT Session" required />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                  <div className="form-group">
                    <label className="form-label">Price ({curr})</label>
                    <input className="form-input" type="number" step="0.01" min="0" value={tierForm.price}
                      onChange={e => setTierForm(f => ({...f, price: e.target.value}))} placeholder="0.00" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Duration (min)</label>
                    <input className="form-input" type="number" min="0" value={tierForm.duration_minutes}
                      onChange={e => setTierForm(f => ({...f, duration_minutes: e.target.value}))} placeholder="e.g. 60" />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" type="submit" disabled={savingTier} style={{ flex: 1 }}>
                    {savingTier ? 'Saving...' : editingTier ? 'Save Changes' : 'Add Tier'}
                  </button>
                  {editingTier && (
                    <>
                      <button type="button" className="btn btn-ghost" style={{ flex: 1 }}
                        onClick={() => { setEditingTier(null); setTierForm({ name: '', price: '', duration_minutes: '' }); }}>
                        Cancel
                      </button>
                      <button type="button" onClick={() => handleDeleteTier(editingTier.id)}
                        style={{ minHeight: 44, padding: '0 14px', background: 'rgba(215,38,56,0.1)', border: '1px solid rgba(215,38,56,0.3)', color: 'var(--color-primary)', borderRadius: 'var(--radius)', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {viewInv && (
        <InvoiceModal
          inv={viewInv}
          trainer={trainer}
          clients={clients}
          onClose={() => setViewInv(null)}
        />
      )}
    </div>
  );
}
